import { UseMutateFunction } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Allocation, AllocationCreateRequest, AllocationType } from '@ndvr/portfolio-api';

import { resolveClonedAllocationType } from 'views/portfolio-engine/allocation-builder/allocations/cloneAllocationType';

import { useAllocationDialogSave } from './useAllocationDialogSave';

interface UsePortfolioAllocationStateProps {
    cardIsEditing: boolean;
    portfolioAllocation?: Allocation;
    allocations: Allocation[];
    onPortfolioAllocationChange: (allocationGUID: string | undefined) => void;
    createAllocation: UseMutateFunction<string | undefined, Error, AllocationCreateRequest, unknown>;
    canCreateSharedAllocation?: boolean;
}

export const usePortfolioAllocationState = ({
    cardIsEditing,
    portfolioAllocation,
    allocations,
    onPortfolioAllocationChange,
    createAllocation,
    canCreateSharedAllocation = false,
}: UsePortfolioAllocationStateProps) => {
    const [allocationTypeFilter, setAllocationTypeFilter] = useState<AllocationType>(AllocationType.SHARED);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingAllocation, setEditingAllocation] = useState<Allocation | undefined>();

    // Initialize filter based on current portfolio allocation when entering edit mode
    useEffect(() => {
        if (cardIsEditing && portfolioAllocation) {
            setAllocationTypeFilter(portfolioAllocation.allocationType);
        }
    }, [cardIsEditing, portfolioAllocation]);

    const handleAllocationTypeFilterChange = (newType: AllocationType) => {
        setAllocationTypeFilter(newType);
        // Clear selection when filter changes
        onPortfolioAllocationChange(undefined);
    };

    const handleCreateAllocation = () => {
        setEditingAllocation(undefined);
        setIsDialogOpen(true);
    };

    const handleCloneAllocation = (portfolioAllocationGUID?: string) => {
        if (portfolioAllocationGUID) {
            const allocationToClone = allocations.find((a) => a.allocationGUID === portfolioAllocationGUID);
            if (allocationToClone) {
                setEditingAllocation({
                    ...allocationToClone,
                    allocationGUID: undefined,
                    allocationName: `${allocationToClone.allocationName} (Copy)`,
                    allocationType: resolveClonedAllocationType(
                        allocationToClone.allocationType,
                        canCreateSharedAllocation,
                    ),
                } as unknown as Allocation);
                setIsDialogOpen(true);
            }
        }
    };

    const handleDialogClose = () => {
        setIsDialogOpen(false);
        setEditingAllocation(undefined);
    };

    const handleAllocationDialogSave = useAllocationDialogSave({
        createAllocation,
        onSuccess: onPortfolioAllocationChange,
        onDialogClose: handleDialogClose,
    });

    return {
        allocationTypeFilter,
        isDialogOpen,
        editingAllocation,
        handleAllocationTypeFilterChange,
        handleCreateAllocation,
        handleCloneAllocation,
        handleDialogClose,
        handleAllocationDialogSave,
    };
};
